// State + API + error classification for the replay error modals.
// Pure TS (no JSX) so it can be imported by load-time code (index.ts,
// downloadAndOpenFile.ts) without pulling the React component in. The modal UI
// lives in ReplayErrorModal.tsx; the Provider wires this state to the view.

import { proxy } from 'valtio'
import { REPLAY_ERRORS, ReplayErrorId, ReplayErrorActionKind } from './replayErrors'

export interface ReplayErrorModalState {
  open: boolean
  errorId: ReplayErrorId
  vars?: Record<string, string>
  code?: string
  onAction?: (kind: ReplayErrorActionKind) => void
}

export const replayErrorModalState = proxy<ReplayErrorModalState>({
  open: false,
  errorId: 'unknown',
})

// Synthesize a short, human-shareable diagnostic code, e.g. `PLB-260630-A3F9K2`.
const genReplayErrorCode = (prefix: string) => {
  const d = new Date()
  const ymd = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase()
  return `${prefix}-${ymd}-${rand}`
}

// Open a replay error modal from anywhere:
//   showReplayError('network')
//   showReplayError('version-unsupported', { vars: { version, maxVersion } })
// The caller may override the button behaviour via `onAction`; otherwise the
// default reload-based actions (runReplayErrorAction) are used.
export const showReplayError = (
  errorId: ReplayErrorId,
  opts: { vars?: Record<string, string>; code?: string; onAction?: (kind: ReplayErrorActionKind) => void } = {},
) => {
  const def = REPLAY_ERRORS[errorId]
  replayErrorModalState.errorId = errorId
  replayErrorModalState.vars = opts.vars
  replayErrorModalState.code = opts.code ?? (def.codePrefix ? genReplayErrorCode(def.codePrefix) : undefined)
  replayErrorModalState.onAction = opts.onAction
  replayErrorModalState.open = true
}

export const hideReplayError = () => {
  replayErrorModalState.open = false
}

// ── Default button actions ──────────────────────────────────────────────────
// The session is already in an error state, so a full reload to a clean slate is
// the most robust choice (clears dirty bot/renderer/world state).
export const runReplayErrorAction = (kind: ReplayErrorActionKind) => {
  if (typeof location === 'undefined') return
  const url = new URL(location.href)
  switch (kind) {
    case 'home':
      // Drop auto-load params (replayFileUrl/texturepack/…) → land on main menu.
      url.search = ''
      location.href = url.toString()
      break
    case 'openNoPack':
      // Retry the same replay but without the resource pack.
      url.searchParams.delete('texturepack')
      location.href = url.toString()
      break
    default:
      // 'retry' / 'restart' — re-run the same load from a clean slate.
      location.reload()
      break
  }
}

// ── Error classification ─────────────────────────────────────────────────────
// Map a thrown error (often wrapped several times: "Failed to start local replay
// server: Error: Replay file is empty") onto one of the modal ids. Returns
// undefined when nothing matches confidently — callers fall back to 'unknown'.
export interface ReplayErrorMatch {
  id: ReplayErrorId
  vars?: Record<string, string>
}

export const classifyReplayError = (err: unknown): ReplayErrorMatch | undefined => {
  const raw = err instanceof Error ? (err.message || String(err)) : String(err)
  const m = raw.toLowerCase()

  // Forbidden / too-new Minecraft version.
  if (m.includes('critical world display') || m.includes('version is not supported')) {
    const version = /version\s+(\S+?)\s+is not supported/i.exec(raw)?.[1]
    const maxVersion = /use version\s+([\d.]+)/i.exec(raw)?.[1]
    return { id: 'version-unsupported', vars: { ...version && { version }, ...maxVersion && { maxVersion } } }
  }

  if (m.includes('replay file is empty') || m.includes('no game data')) {
    return { id: 'empty-replay' }
  }

  // Bad texture archive (corrupt / HTML / not a ZIP) — check before the generic
  // download-failure rule so it wins for texturepack-specific failures.
  if (m.includes('not a zip archive') || m.includes('instead of a zip') || (m.includes('texturepack') && m.includes('html'))) {
    return { id: 'texturepack-invalid' }
  }
  if (m.includes('failed to download texturepack')) {
    return { id: 'texturepack-network' }
  }

  // Replay download / generic network failure.
  if (
    m.includes('failed to download replay')
    || m.includes('failed to download/open replay')
    || m.includes('failed to fetch')
    || m.includes('networkerror')
    || m.includes('load failed')
    || m.includes('err_internet_disconnected')
  ) {
    return { id: 'network' }
  }

  if (m.includes('replay stopped due to error') || m.includes('already waiting for packets')) {
    return { id: 'playback' }
  }

  // Incompatible replay file format (our header version, not the MC version).
  if (m.includes('unsupported format version') || m.includes('invalid json in file header')) {
    return { id: 'file-open' }
  }

  return undefined
}
